import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { GiCancel } from "react-icons/gi";
import moment from "moment";
import { toast } from "react-toastify";

import styles from "./SamplePaymentModal.module.scss";
import {
  ISamplePaymentFormInput,
  SamplePaymentProps,
  SamplesProps,
} from "../../types/sample";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";
import useConfirmPopup from "../../context/useConfirmPopup";

type Props = {
  sample: SamplesProps;
  onClose: () => void;
  onChanged: () => void;
};

const SamplePaymentModal = ({ sample, onClose, onChanged }: Props) => {
  const confirmDelete = useConfirmPopup();

  const [payments, setPayments] = useState<SamplePaymentProps[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [pendingAmount, setPendingAmount] = useState<number>(0);
  const [extraAmount, setExtraAmount] = useState<number>(0);
  const [paymentStatus, setPaymentStatus] = useState<number>(0);

  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  // The samples list slice empties `state.samples` while a refetch is pending, so
  // refreshing on every entry would flash the table blank behind this modal.
  // Collect the changes and tell the parent once, on close.
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<ISamplePaymentFormInput>({
    defaultValues: {
      amount: null,
      note: "",
      date: moment().format("YYYY-MM-DDTHH:mm"),
    },
  });

  const applySummary = (data: any) => {
    setTotalAmount(data?.total_amount || 0);
    setPaidAmount(data?.paid_amount || 0);
    setPendingAmount(data?.pending_amount || 0);
    setExtraAmount(data?.extra_amount || 0);
    setPaymentStatus(data?.payment_status || 0);
  };

  const fetchPayments = async () => {
    setIsListLoading(true);
    try {
      const response: any = await GET(
        `/v1/admin/sample-orders/${sample.id}/payments`,
        {}
      );
      if (response.data.status === "success") {
        setPayments(response.data.data.records || []);
        applySummary(response.data.data);
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsListLoading(false);
    }
  };

  const handleOnSubmit: SubmitHandler<ISamplePaymentFormInput> = async (
    data: any
  ) => {
    setIsLoading(true);
    try {
      const response: any = await POST(
        `/v1/admin/sample-orders/${sample.id}/payments`,
        {
          amount: data.amount,
          note: data.note || null,
          date: data.date,
        }
      );
      if (response.data.status === "success") {
        setHasChanges(true);
        reset({
          amount: null,
          note: "",
          date: moment().format("YYYY-MM-DDTHH:mm"),
        });
        await fetchPayments();
        toast.success(response.data.message, {
          ...(toastStyles as any),
        });
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!"
    );

    if (!confirmed) return;

    setIsActionLoading(id);
    try {
      const response: any = await DELETE(
        `/v1/admin/sample-orders/${sample.id}/payments/${id}`,
        {}
      );
      if (response.data.status === "success") {
        setHasChanges(true);
        await fetchPayments();
        toast.success(response.data.message, {
          ...(toastStyles as any),
        });
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsActionLoading(0);
    }
  };

  const handleToggleStatus = async (checked: boolean) => {
    const nextStatus = checked ? 1 : 0;
    const previousStatus = paymentStatus;

    setPaymentStatus(nextStatus);

    try {
      const response: any = await PUT(
        `/v1/admin/sample-orders/${sample.id}/payment-status`,
        { payment_status: nextStatus }
      );
      if (response.data.status === "success") {
        setHasChanges(true);
      } else {
        setPaymentStatus(previousStatus);
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      setPaymentStatus(previousStatus);
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    }
  };

  const handleCloseModal = () => {
    if (hasChanges) onChanged();
    onClose();
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sample.id]);

  return (
    <div className={styles.SamplePaymentModal}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-[80vw] lg:h-fit lg:min-h-[calc(100vh-80px)] shadow-lg lg:rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-2xl mt-10 lg:mt-0 text-center lg:text-left font-bold leading-6 text-primary-1">
            સેમ્પલ ના પેમેન્ટ ની વિગતો
          </h3>

          <GiCancel
            onClick={handleCloseModal}
            className="absolute cursor-pointer z-10 shadow bg-white top-3 right-5 text-3xl lg:text-xl text-primary-1 border-2 border-primary-1 p-1 lg:p-0 rounded-full"
          />

          <div className="mt-2 py-3 lg:py-2">
            <div className="p-4 pt-1 pb-0">
              <div className="grid lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-primary-1 font-bold">પાર્ટી :</label>
                  <p className="text-primary-1 font-semibold text-xl">
                    {sample?.party_name || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-primary-1 font-bold">આઈટમ :</label>
                  <p className="text-primary-1 font-semibold text-xl">
                    {sample?.item_name || "-"}
                    {sample?.process_name ? ` - ${sample.process_name}` : ""}
                  </p>
                </div>
                <div>
                  <label className="text-primary-1 font-bold">બિલ નં. :</label>
                  <p className="text-primary-1 font-semibold text-xl">
                    {sample?.id || "-"}
                  </p>
                </div>
              </div>

              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider">
                        Sr. No.
                      </th>
                      <th className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider">
                        તારીખ
                      </th>
                      <th className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider">
                        અમાઉન્ટ
                      </th>
                      <th className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider">
                        નોંધ
                      </th>
                      <th className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider">
                        એકશન્સ બટન
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isListLoading ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          રાહ જુઓ...
                        </td>
                      </tr>
                    ) : (
                      <>
                        {payments.length > 0 ? (
                          <>
                            {payments.map((payment, index) => (
                              <tr key={payment.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {index + 1}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {payment.date
                                    ? moment(payment.date).format(
                                        "DD-MM-YYYY hh:mm A"
                                      )
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                  {(payment.amount || 0).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {payment.note || "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  {isActionLoading === payment.id ? (
                                    <p className="text-primary-2 font-bold text-right text-lg">
                                      રાહ જુઓ...
                                    </p>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        handleDeletePayment(payment.id)
                                      }
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </>
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-primary-2 font-bold text-xl text-center py-2"
                            >
                              પેમેન્ટ ની વિગતો મળી નથી
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="grid lg:grid-cols-3 gap-2 mt-6">
                <h3 className="text-xl text-left font-bold leading-8 text-primary-1">
                  ટોટલ અમાઉન્ટ : Rs. {(totalAmount || 0).toFixed(2)}
                </h3>
                <h3 className="text-xl text-left font-bold leading-8 text-primary-1">
                  જમા થયેલ અમાઉન્ટ : Rs. {(paidAmount || 0).toFixed(2)}
                </h3>
                {extraAmount > 0 ? (
                  <h3 className="text-xl text-left font-bold leading-8 text-orange-600">
                    વધારે જમા અમાઉન્ટ : Rs. {(extraAmount || 0).toFixed(2)}
                  </h3>
                ) : (
                  <h3 className="text-xl text-left font-bold leading-8 text-primary-1">
                    બાકી અમાઉન્ટ : Rs. {(pendingAmount || 0).toFixed(2)}
                  </h3>
                )}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <input
                  id="sample-payment-status"
                  type="checkbox"
                  className="w-5 h-5 accent-primary-1 cursor-pointer"
                  checked={paymentStatus === 1}
                  onChange={(e) => handleToggleStatus(e.target.checked)}
                />
                <label
                  htmlFor="sample-payment-status"
                  className="text-primary-1 font-bold cursor-pointer"
                >
                  પેમેન્ટ પૂરું જમા થઇ ગયું છે
                </label>
              </div>

              <form onSubmit={handleSubmit(handleOnSubmit)} className="mt-6">
                <div className="grid lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-primary-1 font-bold">
                      પેમેન્ટ ની તારીખ :
                    </label>
                    <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                      <input
                        className="bg-white text-primary-1 placeholder:text-primary-2/50 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px] ml-[2px]"
                        type="datetime-local"
                        {...register("date", { required: true })}
                      />
                    </div>
                    {errors.date && (
                      <span className="text-red-500">
                        પેમેન્ટ ની તારીખ સિલેક્ટ કરો
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="text-primary-1 font-bold">
                      પેમેન્ટ નું અમાઉન્ટ :
                    </label>
                    <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-[44px] w-full relative rounded">
                      <input
                        className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2/50 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        type="number"
                        step={"any"}
                        placeholder="પેમેન્ટ નું અમાઉન્ટ દાખલ કરો"
                        {...register("amount", {
                          required: true,
                          min: 0.01,
                        })}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                      />
                    </div>
                    {errors.amount && (
                      <span className="text-red-500">
                        પેમેન્ટ નું અમાઉન્ટ દાખલ કરો
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="text-primary-1 font-bold">
                      પેમેન્ટ ની નોંધ :
                    </label>
                    <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-[44px] w-full relative rounded">
                      <input
                        className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2/50 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        type="text"
                        placeholder="પેમેન્ટ ની નોંધ દાખલ કરો"
                        {...register("note")}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                  </button>
                </div>

                <div className="mt-1 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="button"
                    onClick={handleCloseModal}
                    disabled={isLoading}
                  >
                    બંધ કરો
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplePaymentModal;
